import React, { useState, useEffect } from 'react';
import { Box, Button, Chip, Container, Dialog, DialogContent, DialogTitle, Divider, Paper, Stack, Typography } from '@mui/material';
import { EditRounded, DeleteRounded, CheckCircleRounded, MopedRounded, RunCircleRounded } from '@mui/icons-material';
import { getFirestore, getDoc, doc, deleteDoc } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { PassportRequest, PassportRequestStatus } from '../../../types/PassportRequest';
import passportRequestConverter from '../../../converters/passportRequestConverter';
import { updatePassportRequest } from '../../../utils/updatePassportRequest';
import { PassportRequestInfoBox } from './PassportRequestInfoBox';
import { useAppSelector } from '../../../store';
import { ROLES_PERMISSIONS_MAPPING } from '../../../security/Role';
import { SecureComponent } from '../../../security/SecureComponent';

export const PassportRequestPage = () => {
  const storage =  getStorage();
  const db = getFirestore();
  const navigate = useNavigate();
  const authState = useAppSelector(s => s.auth);

  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const { state } = useLocation();

  // const adminOnly = authState.URB.roles.some((r: string) => ['super', 'admin'].includes(r));
  // const hasPermission = Permissions.hasPermission(authState, )

  const [passportRequest, setPassportRequest] = useState<PassportRequest>((state as any)?.passportRequest ?? undefined);
  const [disableDelete, setDisableDelete] = useState(false);

  const [showForm, setShowForm] = useState(false);
  
  const updateStatus = (status: PassportRequestStatus) => {
    if (window.confirm(`Confirm to set status to '${status}'?`)) {
      updatePassportRequest(passportRequest, status).then(() => {
        getDoc(doc(db, `Employees/${passportRequest.idNumber}/PassportRequests/${id}`).withConverter(passportRequestConverter)).then(docSnapshot => {
          if (docSnapshot.exists()) {
            setPassportRequest(docSnapshot.data());
          }
        });
        enqueueSnackbar(`Passport request has been set to '${status}'!`, { variant: 'success' });
      });
    }
  };

  const handleDeleteOnClick = () => {
    if (window.confirm('Are you sure you want to delete this passport request?')) {
      setDisableDelete(true);
      deleteDoc(doc(db, `Employees/${passportRequest.idNumber}/PassportRequests/${passportRequest.id}`))
        .then(() => {
          enqueueSnackbar('Passport request has been deleted.', { variant: 'success' });
        })
        .finally(() => {
          setDisableDelete(false);
          setTimeout(() => navigate(-1), 250);
      });
    }
  };

  const openForm = () => setShowForm(true);
  const closeForm = () => setShowForm(false);
  const handleSetProcessingOnClick = () => updateStatus('Processing');
  const handleSetAvailableOnClick = () => updateStatus('Available');
  const handleSetCompletedOnClick = () => updateStatus('Completed');

  useEffect(() => {
    if (!state || !(state as any)?.passportRequest) {
      getDoc(doc(db, `PassportRequests/${id}`).withConverter(passportRequestConverter)).then(docSnapshot => {
        if (docSnapshot.exists()) {
          setPassportRequest(docSnapshot.data());
        }
      });
    }
  }, []);

  if (!passportRequest) {
    return (
      <Typography variant="caption">Please wait...</Typography>
    );
  }

  return (
    <>
      <Container disableGutters>
        <Stack spacing={3} direction="row" alignItems="center" mb={2}>
          <Chip label={passportRequest.idNumber} />
          <Typography variant="h6">{passportRequest.name}</Typography>
          <Stack direction='row' spacing={1} sx={{ display: 'flex', flexGrow: 1, justifyContent: 'right' }}>
            <SecureComponent permissions={ROLES_PERMISSIONS_MAPPING['editor.passport']}>
              <Button startIcon={<DeleteRounded />} color="error" disabled={disableDelete} onClick={handleDeleteOnClick}>DELETE</Button>
            </SecureComponent>
          </Stack>
        </Stack>
        <Paper>
          <PassportRequestInfoBox passportRequest={passportRequest} />
        </Paper>
        <SecureComponent permissions={ROLES_PERMISSIONS_MAPPING['editor.passport']}>
          <Stack mt={1} spacing={1} direction="row" justifyContent="right">
            <Button 
              color="warning" 
              startIcon={<RunCircleRounded />} 
              disabled={passportRequest.status !== 'New'}
              onClick={handleSetProcessingOnClick}
            >
              Processing
            </Button>
            <Button 
              color="secondary" 
              startIcon={<MopedRounded />}
              disabled={passportRequest.status !== 'New' && passportRequest.status !== 'Processing'}
              onClick={handleSetAvailableOnClick}
            >
              For Pickup
            </Button>
            <Button 
              color="success" 
              startIcon={<CheckCircleRounded />} 
              disabled={passportRequest.status === 'Completed' || passportRequest.status === 'Cancelled'}
              onClick={handleSetCompletedOnClick}
            >
              Completed
            </Button>
          </Stack>
        </SecureComponent>
      </Container>
      <Dialog open={showForm} onClose={closeForm} fullWidth>
        <DialogTitle>Update Passport Request</DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
        </DialogContent>
      </Dialog>
    </>
  );
};
